// BulkPhotoUpload.jsx — admin bulk product-photo upload (Brian, 2026-07-13).
// Drop in dozens of photos NAMED BY SKU ("10001.jpg", "10061 tofu.png"...);
// each file is auto-matched to its product, compressed, and uploaded through
// the same pipeline as the single-photo uploader (Storage product-images/
// {sku}.jpg + imageUrl on the product doc). Unmatched filenames are listed
// so they can be renamed and retried. Uploads run one at a time.
import { useState } from "react";
import { storage, db } from "../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { invalidateProductCache } from "../services/dataCache";

import { compressImage } from "../utils/compressImage";

// "10061 tofu (2).png" -> sku candidate "10061"; "136085-1.jpg" -> "136085-1"
function skuFromFilename(filename, skuSet) {
  const base = filename.replace(/\.[^.]+$/, "").trim();
  if (skuSet.has(base)) return base;
  const firstToken = (base.match(/^[A-Za-z0-9-]+/) || [])[0];
  if (firstToken && skuSet.has(firstToken)) return firstToken;
  return null;
}

export default function BulkPhotoUpload({ products, onUploaded }) {
  const [matched, setMatched] = useState([]);   // { file, sku, name, hasPhoto }
  const [unmatched, setUnmatched] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [failures, setFailures] = useState([]);
  const [doneMsg, setDoneMsg] = useState("");

  function handleFiles(e) {
    const files = [...(e.target.files || [])];
    const bySku = Object.fromEntries(products.map(p => [p.sku, p]));
    const skuSet = new Set(products.map(p => p.sku));
    const m = [], u = [];
    for (const file of files) {
      const sku = skuFromFilename(file.name, skuSet);
      if (sku) m.push({ file, sku, name: bySku[sku].name, hasPhoto: !!bySku[sku].imageUrl });
      else u.push(file.name);
    }
    setMatched(m);
    setUnmatched(u);
    setFailures([]);
    setDoneMsg("");
  }

  async function handleUpload() {
    if (uploading || matched.length === 0) return;
    setUploading(true);
    setFailures([]);
    setDoneMsg("");
    let done = 0;
    const failed = [];
    for (const { file, sku } of matched) {
      try {
        const compressed = await compressImage(file);
        const storageRef = ref(storage, `product-images/${sku}.jpg`);
        await uploadBytes(storageRef, compressed);
        const url = await getDownloadURL(storageRef);
        await updateDoc(doc(db, "products", sku), { imageUrl: url });
        onUploaded && onUploaded(sku, url);
      } catch (err) {
        failed.push(`${sku} (${file.name}): ${err.message}`);
      }
      done++;
      setProgress({ done, total: matched.length });
    }
    invalidateProductCache();
    setFailures(failed);
    setDoneMsg(`Uploaded ${matched.length - failed.length} of ${matched.length} photo${matched.length !== 1 ? "s" : ""}.`);
    setMatched([]);
    setProgress(null);
    setUploading(false);
  }

  const replacing = matched.filter(m => m.hasPhoto).length;

  return (
    <div className="bulk-photo-upload">
      <h3>Bulk photo upload</h3>
      <p className="inventory-help-note">
        Name each photo file by its SKU (e.g. <strong>10001.jpg</strong> — extra words after
        the SKU are fine) and select them all at once. Photos are matched, compressed, and
        attached to their products automatically. Re-uploading a SKU replaces its photo.
      </p>
      <input
        type="file"
        accept="image/*"
        multiple
        disabled={uploading}
        onChange={handleFiles}
      />

      {(matched.length > 0 || unmatched.length > 0) && !uploading && !doneMsg && (
        <div className="bulk-photo-summary">
          <p>
            <strong>{matched.length}</strong> photo{matched.length !== 1 ? "s" : ""} matched
            {replacing > 0 && <> ({replacing} will replace an existing photo)</>}
            {unmatched.length > 0 && <> · <span className="error">{unmatched.length} not matched</span></>}
          </p>
          {matched.slice(0, 8).map(m => (
            <p key={m.sku} className="bulk-photo-row">✔ {m.sku} — {m.name}{m.hasPhoto ? " (replaces current)" : ""}</p>
          ))}
          {matched.length > 8 && <p className="bulk-photo-row">…and {matched.length - 8} more</p>}
          {unmatched.map(n => (
            <p key={n} className="bulk-photo-row error">✘ {n} — no product with that SKU; rename the file and reselect</p>
          ))}
          {matched.length > 0 && (
            <button onClick={handleUpload}>
              Upload {matched.length} photo{matched.length !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      )}

      {uploading && progress && (
        <p className="inventory-success">Uploading… {progress.done} / {progress.total}</p>
      )}
      {doneMsg && <p className="inventory-success">{doneMsg}</p>}
      {failures.map(f => <p key={f} className="error">{f}</p>)}
    </div>
  );
}
